import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { useEffect, useRef, useState } from "react";
import { Bookmark, BookmarkCheck, Heart, Info, LoaderCircle } from "lucide-react";
import type { DiscoveryCardModel } from "./types";
import { compactAppDescription } from "./discoveryCatalogue";

/**
 * Right after the name in the app window bar: the details modal (authors,
 * description, statistics, comments) and the description's first line,
 * muted and truncated, so switching apps still says what each one does.
 */
export function AppTitleDetails({ title, entry, onDetails }: {
  title: string;
  entry: DiscoveryCardModel | null;
  onDetails: () => void;
}) {
  useLocale();
  const tagline = compactAppDescription(entry?.description ?? "").split(/(?<=[.!?。！？])\s/, 1)[0];
  const rating = entry?.aiRating;
  return (
    <div className="discovery-window-title-details">
      <button
        type="button"
        title={t("App details")}
        aria-label={t("Details for {{value0}}", { value0: title })}
        aria-haspopup="dialog"
        onClick={onDetails}
      >
        <Info size={19} aria-hidden="true" />
      </button>
      {tagline ? <span className="discovery-window-tagline" title={tagline}>{tagline}</span> : null}
      {rating?.average != null ? (
        <span
          className="discovery-window-rating"
          title={t("AI review for {{value0}}: {{value1}} out of 5, {{value2}} rated reviews", {
            value0: title,
            value1: rating.average.toFixed(1),
            value2: rating.rated_count,
          })}
        >
          ★ {rating.average.toFixed(1)}
        </span>
      ) : null}
    </div>
  );
}

const SAVE_EXPLAINED_KEY = "ato.library.saveExplained.v1";
const saveExplained = () => { try { return localStorage.getItem(SAVE_EXPLAINED_KEY) === "1"; } catch { return false; } };

/**
 * The app window bar's actions: a bookmark saves the app to Library
 * (outline → saving → filled), the heart is a public Like. The first save
 * explains that it makes your own copy. A saved bookmark stays filled and
 * opens that copy; it never removes it. A failure tints it and carries the
 * reason in its title.
 */
export function AppHeaderActions({
  title,
  entry,
  signedIn,
  installing,
  installResult = null,
  installError = null,
  liking,
  onInstall,
  onLike,
}: {
  title: string;
  entry: DiscoveryCardModel | null;
  signedIn: boolean;
  installing: boolean;
  /** The last save of this app failed (its reason in installError). */
  installResult?: "installed" | "failed" | null;
  installError?: string | null;
  liking: boolean;
  /** Save, or for a saved app open its Library copy. */
  onInstall: () => void;
  onLike: () => void;
}) {
  useLocale();
  const listingId = entry?.selection.kind === "discover" ? entry.selection.id : null;
  const [explaining, setExplaining] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!explaining) return;
    const outside = (event: PointerEvent) => { if (!wrap.current?.contains(event.target as Node)) setExplaining(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setExplaining(false); };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [explaining]);
  const saved = Boolean(entry?.added);
  const failed = !saved && installResult === "failed";
  const save = () => {
    if (saved || saveExplained()) { onInstall(); return; }
    setExplaining(true);
  };
  return (
    <div className="discovery-window-actions">
      {listingId ? (
        <span className="discovery-window-save-wrap" ref={wrap}>
          <button
            type="button"
            className={`discovery-window-save${saved ? " discovery-window-save--saved" : ""}${failed ? " discovery-window-save--failed" : ""}`}
            title={installing ? t("Saving to Library…") : saved ? t("Saved to Library") : failed ? (installError ?? t("The app could not be added.")) : t("Save to Library")}
            aria-label={saved ? t("{{value0}} is in Library", { value0: title }) : t("Add {{value0}} to Library", { value0: title })}
            aria-pressed={saved}
            aria-busy={installing}
            disabled={installing}
            onClick={save}
          >
            {installing
              ? <LoaderCircle size={20} className="discovery-spinner" aria-hidden="true" />
              : saved
                ? <BookmarkCheck size={20} aria-hidden="true" />
                : <Bookmark size={20} aria-hidden="true" />}
          </button>
          {explaining ? (
            <span className="discovery-save-explainer" role="dialog" aria-label={t("Save to Library")}>
              <span><Message message="Save to Library makes your own copy of this app. Work in this Try is not carried over." /></span>
              <span className="discovery-save-explainer-actions">
                <button type="button" onClick={() => setExplaining(false)}><Message message="Cancel" /></button>
                <button type="button" className="discovery-save-explainer-confirm" onClick={() => {
                  try { localStorage.setItem(SAVE_EXPLAINED_KEY, "1"); } catch { /* the explanation shows again next time */ }
                  setExplaining(false);
                  onInstall();
                }}><Message message="Save to Library" /></button>
              </span>
            </span>
          ) : null}
        </span>
      ) : null}
      <button
        type="button"
        className="discovery-window-like"
        title={entry?.viewer?.liked ? t("Remove your public like") : t("Like publicly")}
        aria-label={t("Like {{value0}}", { value0: title })}
        aria-pressed={entry?.viewer?.liked ?? false}
        aria-busy={liking}
        disabled={liking || !listingId || (signedIn && entry?.viewer?.canLike !== true)}
        onClick={onLike}
      >
        {liking ? (
          <LoaderCircle size={20} className="discovery-spinner" aria-hidden="true" />
        ) : (
          <Heart size={20} fill={entry?.viewer?.liked ? "currentColor" : "none"} aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
